import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator as CalculatorIcon, Info } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import SEOHead from '../components/SEOHead';

// Luxury car tax (LCT) maths, per the ATO.
//
//   LCT = (LCT value - LCT threshold) x 10/11 x 33%
//
// LCT is charged at 33%, but only on the amount ABOVE the threshold, and the
// 10/11 factor strips the GST component out of that excess before the 33% is
// applied. Source: ATO "Working out the LCT on a sale" (last updated
// 1 June 2026) and "Luxury car tax rate and thresholds".
//
// ATO worked example, reproduced exactly by this function:
//   $88,000 car, 2026-27, not fuel-efficient, threshold $80,809
//   ($88,000 - $80,809) x 10/11 x 33% = $2,157.30
const LCT_RATE = 0.33;

// ATO thresholds by financial year. Verified against
// ato.gov.au/tax-rates-and-codes/luxury-car-tax-rate-and-thresholds (2026-07-28).
// `feFrom` marks the year the fuel-efficient definition tightened: from
// 1 July 2025 a fuel-efficient car must use no more than 3.5 L/100km combined;
// before that the limit was 7.0 L/100km.
const THRESHOLDS = [
  { year: '2026-27', fuelEfficient: 91661, other: 80809 },
  { year: '2025-26', fuelEfficient: 91387, other: 80567 },
  { year: '2024-25', fuelEfficient: 91387, other: 80567 },
  { year: '2023-24', fuelEfficient: 89332, other: 76950 },
  { year: '2022-23', fuelEfficient: 84916, other: 71849 },
];

const FUEL_LIMIT_FROM_2025_26 = 3.5;
const FUEL_LIMIT_BEFORE_2025_26 = 7.0;

const fuelLimitForYear = (year: string): number =>
  year === '2026-27' || year === '2025-26' ? FUEL_LIMIT_FROM_2025_26 : FUEL_LIMIT_BEFORE_2025_26;

const calcLct = (lctValue: number, threshold: number) => {
  const excess = lctValue - threshold;
  if (excess <= 0) return { lct: 0, excess: 0 };
  return { lct: (excess * 10) / 11 * LCT_RATE, excess };
};

const LuxuryCarTaxCalculator: React.FC = () => {
  const [carPrice, setCarPrice] = useState('88000');
  const [extras, setExtras] = useState('0');
  const [year, setYear] = useState('2026-27');
  const [isFuelEfficient, setIsFuelEfficient] = useState('no');

  const [lct, setLct] = useState(0);
  const [excess, setExcess] = useState(0);
  const [threshold, setThreshold] = useState(0);
  const [lctValue, setLctValue] = useState(0);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const price = parseFloat(carPrice);
    const extra = extras.trim() === '' ? 0 : parseFloat(extras);
    const band = THRESHOLDS.find((t) => t.year === year);

    if (isNaN(price) || price <= 0 || isNaN(extra) || extra < 0 || !band) {
      setValid(false);
      setLct(0);
      return;
    }

    const value = price + extra;
    const applicable = isFuelEfficient === 'yes' ? band.fuelEfficient : band.other;
    const result = calcLct(value, applicable);

    setValid(true);
    setLctValue(value);
    setThreshold(applicable);
    setExcess(result.excess);
    setLct(result.lct);
  }, [carPrice, extras, year, isFuelEfficient]);

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const fuelLimit = fuelLimitForYear(year);
  const headroom = threshold - lctValue;

  return (
    <>
      <SEOHead
        title="Luxury Car Tax Calculator Australia | LCT Thresholds 2026-27"
        description="Work out the luxury car tax on your vehicle with current ATO thresholds. Free LCT calculator for Australia covering fuel-efficient and other cars, 2022-23 to 2026-27."
        keywords="luxury car tax calculator, lct calculator australia, luxury car tax threshold 2026-27, how much luxury car tax will i pay, lct rate australia, fuel efficient car lct threshold"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "Luxury Car Tax Calculator Australia",
              "description": "Calculate the luxury car tax (LCT) payable on a vehicle in Australia using current ATO thresholds and the 33% LCT rate.",
              "applicationCategory": "FinanceApplication",
              "operatingSystem": "Any",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "AUD" },
              "areaServed": { "@type": "Country", "name": "Australia" }
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is the luxury car tax threshold for 2026-27?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "For the 2026-27 financial year the luxury car tax thresholds are $91,661 for fuel-efficient vehicles and $80,809 for all other vehicles. If the GST-inclusive value of the car is at or below the threshold that applies to it, no LCT is payable. LCT is only charged on the amount above the threshold, not on the whole price."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How is luxury car tax calculated in Australia?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "The ATO formula is (LCT value minus LCT threshold) multiplied by 10/11, multiplied by 33%. The 10/11 factor removes the GST component from the amount above the threshold before the 33% rate is applied. For example, an $88,000 car that is not fuel-efficient in 2026-27 exceeds the $80,809 threshold by $7,191, which gives $7,191 x 10/11 x 33% = $2,157.30 of LCT."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What counts as a fuel-efficient car for luxury car tax?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "From 1 July 2025 a fuel-efficient car is one with a combined fuel consumption rating of no more than 3.5 litres per 100 kilometres. Before 1 July 2025 the limit was 7 litres per 100 kilometres. Fuel-efficient cars get a higher LCT threshold, so the tightened definition means fewer vehicles now qualify for the concession."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is stamp duty or registration included in the LCT value?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No. The LCT value includes GST, customs duty, dealer delivery charges, standard and statutory warranties, and accessories or modifications fitted before delivery. It excludes stamp duty, transfer fees, registration, compulsory third-party insurance, extended warranties, service plans and the cost of financing the purchase."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can you claim back or avoid luxury car tax?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "LCT is generally payable by the business that sells or imports the car and is passed on in the price. Limited exemptions and refund entitlements exist, including for certain primary producers and tourism operators, some emergency vehicles, and cars exported or supplied in specific circumstances. Whether one applies depends on your situation, so check the ATO guidance or speak to your accountant."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does luxury car tax apply to used cars?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "LCT can apply to a used car, but only if it is less than two years old and its value still exceeds the threshold. LCT already paid on the vehicle can reduce the amount payable, so tax is not charged twice on the same value. Private sales between individuals who are not registered for GST do not attract LCT."
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Luxury Car Tax Calculator"
        description="Work out the luxury car tax payable on a vehicle in Australia using the ATO's current thresholds and the 33% LCT rate."
        icon={CalculatorIcon}
        result={valid ? (lct > 0 ? `Luxury car tax payable: ${formatCurrency(lct)}` : 'No luxury car tax payable') : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                Luxury car tax is charged at 33%, but only on the amount <strong>above</strong> the threshold — not on the whole
                price. Enter the GST-inclusive price of the car; leave out stamp duty, registration and CTP.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="Car Price (including GST)"
              value={carPrice}
              onChange={setCarPrice}
              unit="$"
              placeholder="e.g. 88000"
            />
            <Input
              label="Dealer Delivery & Accessories"
              value={extras}
              onChange={setExtras}
              unit="$"
              placeholder="e.g. 2500"
            />
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                {THRESHOLDS.map((t) => (
                  <option key={t.year} value={t.year}>{t.year}</option>
                ))}
              </select>
            </div>
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuel-Efficient Vehicle?</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={isFuelEfficient}
                onChange={(e) => setIsFuelEfficient(e.target.value)}
              >
                <option value="no">No — standard threshold</option>
                <option value="yes">Yes — {fuelLimit} L/100km or less</option>
              </select>
            </div>
          </div>

          {valid && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h2 className="text-xl font-semibold">Your Luxury Car Tax Result</h2>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-blue-700">LCT Payable</div>
                    <div className="text-2xl font-bold text-blue-800">{formatCurrency(lct)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Threshold Applied</div>
                    <div className="text-2xl font-bold text-gray-800">{formatCurrency(threshold)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Amount Over Threshold</div>
                    <div className="text-2xl font-bold text-gray-800">{formatCurrency(excess)}</div>
                  </div>
                </div>

                {lct > 0 ? (
                  <div className="bg-green-50 p-4 rounded-lg space-y-2">
                    <p className="text-sm text-green-800">
                      On an LCT value of <strong>{formatCurrency(lctValue)}</strong>, the {year} threshold of{' '}
                      {formatCurrency(threshold)} is exceeded by <strong>{formatCurrency(excess)}</strong>, so the tax is{' '}
                      {formatCurrency(excess)} &times; 10/11 &times; 33% = <strong>{formatCurrency(lct)}</strong>.
                    </p>
                    <p className="text-sm text-green-800">
                      Total price including LCT: <strong>{formatCurrency(lctValue + lct)}</strong> — before stamp duty,
                      registration and CTP, which are added separately.
                    </p>
                  </div>
                ) : (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-800">
                      No luxury car tax is payable. An LCT value of <strong>{formatCurrency(lctValue)}</strong> sits{' '}
                      <strong>{formatCurrency(Math.abs(headroom))}</strong> below the {year} threshold of{' '}
                      {formatCurrency(threshold)}.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Threshold reference table */}
          <div className="mt-8 bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">LCT thresholds by financial year</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold text-gray-700">Financial year</th>
                    <th className="text-right px-6 py-3 font-semibold text-gray-700">Fuel-efficient vehicles</th>
                    <th className="text-right px-6 py-3 font-semibold text-gray-700">Other vehicles</th>
                  </tr>
                </thead>
                <tbody>
                  {THRESHOLDS.map((t) => (
                    <tr key={t.year} className="border-t border-gray-100">
                      <td className="px-6 py-3 text-gray-700">{t.year}</td>
                      <td className="px-6 py-3 text-right text-gray-700">
                        ${t.fuelEfficient.toLocaleString('en-AU')}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-700">
                        ${t.other.toLocaleString('en-AU')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-6 py-3 text-xs text-gray-500">
              Source: Australian Taxation Office, luxury car tax rate and thresholds.
            </p>
          </div>

          {/* Educational content */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">How luxury car tax is calculated</h2>
            <p className="text-gray-600">
              LCT applies when the GST-inclusive value of a car is above the LCT threshold for the year it was imported,
              acquired or sold. The ATO formula is <strong>(LCT value &minus; LCT threshold) &times; 10/11 &times; 33%</strong>.
              The 10/11 factor strips the GST out of the amount above the threshold before the 33% rate is applied, which is why
              the tax is always less than a third of the excess. Because LCT is charged on the value above the threshold only, a
              car priced just over the limit attracts very little tax.
            </p>

            <h3 className="text-lg font-semibold text-gray-800">What goes into the LCT value</h3>
            <p className="text-gray-600">The LCT value <strong>includes</strong>:</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              <li>GST and any customs duty</li>
              <li>Dealer delivery charges</li>
              <li>Standard and statutory warranties</li>
              <li>Accessories, modifications and treatments fitted before delivery</li>
            </ul>
            <p className="text-gray-600">It <strong>excludes</strong>:</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              <li>Stamp duty, transfer fees and registration</li>
              <li>Compulsory third-party insurance (CTP)</li>
              <li>Extended warranties and service plans</li>
              <li>The cost of financing the purchase</li>
            </ul>
            <p className="text-gray-600">
              Because stamp duty is excluded from the LCT value but still payable on top, budget for it separately with our{' '}
              <Link className="text-blue-600 hover:underline" to="/stamp-duty-calculator/">stamp duty calculator</Link>, and model
              the repayments on the financed amount with the{' '}
              <Link className="text-blue-600 hover:underline" to="/car-loan-calculator/">car loan repayment calculator</Link>.
            </p>

            <h3 className="text-lg font-semibold text-gray-800">The fuel-efficient threshold changed in 2025</h3>
            <p className="text-gray-600">
              Fuel-efficient cars get a higher threshold. From 1 July 2025 a car only qualifies if its combined fuel consumption
              is <strong>3.5 L/100km or less</strong> — down from 7 L/100km. That is a significant tightening: many hybrids that
              qualified under the old rule no longer do, which pushes them onto the lower standard threshold and can add
              thousands to the tax payable. Fully electric vehicles comfortably meet the limit. If you are weighing up an EV,
              our guide to{' '}
              <Link className="text-blue-600 hover:underline" to="/blog/electric-vehicle-car-loans-australia/">
                electric vehicle car loans in Australia
              </Link>{' '}
              covers the finance side.
            </p>

            <h2 className="text-xl font-semibold text-gray-800">Frequently asked questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">What is the luxury car tax threshold for 2026-27?</h3>
                <p className="text-gray-600">
                  $91,661 for fuel-efficient vehicles and $80,809 for all other vehicles. If the GST-inclusive value of the car
                  is at or below the threshold that applies to it, no LCT is payable.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">How is luxury car tax calculated in Australia?</h3>
                <p className="text-gray-600">
                  Using the ATO formula (LCT value &minus; LCT threshold) &times; 10/11 &times; 33%. An $88,000 car that is not
                  fuel-efficient in 2026-27 exceeds the $80,809 threshold by $7,191, giving $7,191 &times; 10/11 &times; 33% =
                  $2,157.30 of LCT.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">What counts as a fuel-efficient car?</h3>
                <p className="text-gray-600">
                  From 1 July 2025, a combined fuel consumption rating of no more than 3.5 L/100km. Before that date the limit
                  was 7 L/100km, so fewer vehicles qualify for the higher threshold now.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Is stamp duty included in the LCT value?</h3>
                <p className="text-gray-600">
                  No. Stamp duty, transfer fees, registration and CTP are all excluded from the LCT value, though you still pay
                  them on top of the purchase price.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Can you avoid or claim back luxury car tax?</h3>
                <p className="text-gray-600">
                  LCT is generally payable by the business selling or importing the car and passed on in the price. Limited
                  exemptions and refunds exist — for example for certain primary producers and tourism operators, and some
                  emergency vehicles. Whether one applies depends on your circumstances, so check the ATO guidance or ask your
                  accountant.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Does LCT apply to used cars?</h3>
                <p className="text-gray-600">
                  It can, but only where the car is less than two years old and still valued above the threshold. LCT already
                  paid reduces the amount payable so the same value is not taxed twice. Private sales between people who are not
                  registered for GST do not attract LCT. If you are buying privately, see our guide to{' '}
                  <Link className="text-blue-600 hover:underline" to="/blog/finance-for-private-sale-car-australia/">
                    finance for a private sale car
                  </Link>.
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 pt-2">
              This calculator provides estimates for general information only and is not tax or financial advice. Thresholds are
              those published by the ATO and are indexed each financial year. Confirm the figures that apply to your purchase
              with the ATO or your accountant.
            </p>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default LuxuryCarTaxCalculator;
